import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useProperty, useSearch } from '@eti/providers';
import { NetworkStatus, useQuery } from '@apollo/client';
import type {
  SearchOnResultPageQuery,
  SelectTripWithDataOnResultPageMutationVariables,
  SortType,
} from '@eti/schema-types';
import { useLocation } from 'react-router-dom';
import SearchTransition from '../components/SearchTransition';
import SearchResultsSkeleton from '../components/v1/skeleton/SearchResultsSkeleton';
import userInfo from '../../../utils/userInfo';
import getFallbackUrl from '../../../utils/getFallbackUrl';
import { ONE_WAY, RETURN } from '../../../constants/tripTypesConstants';
import { RESULT_PAGE_VERSION_3 } from '../constants/resultConstants';
import { quickSortType } from '../../../constants/quickSortConstants';
import { SEARCH_QUERY, SEARCH_MORE_QUERY } from '../graphql/queries';
import type { TripDataType } from '../../../common/custom-hooks/save-trips/models';
import type { TransformSearchResultDataReturnType } from '../utils/searchDetails';
import type { SearchOnResultPageQuickSortPricesType } from '../models';

const SearchResults = lazy(() => import('../components/v1/SearchResults'));
const SearchResultsV3 = lazy(() => import('../components/v3/SearchResults'));
const ResultSkeletonV3 = lazy(() => import('../components/v3/skeleton/ResultSkeleton'));

const transformQuickSortPrices = (
  quickSortPrices: SearchOnResultPageQuickSortPricesType,
): { [p: string]: number } =>
  Object.fromEntries(Object.entries(quickSortPrices).map(([key, item]) => [key, item.value]));

// TODO: redirectToDBF, redirectToTravelerDetails, selectedSortType, shouldShowLoadingSpinner to not be optional
export interface SearchResultsGraphqlContainerProps {
  addSavedTrip: (tripData: TripDataType) => void;
  addTrip: (
    variables: {
      variables: SelectTripWithDataOnResultPageMutationVariables;
    },
    getAncillaryBundles: () => void,
    hasPreSelectedProducts?: boolean,
  ) => Promise<void>;
  closeFiltersPanel: () => void;
  handleSearch: (keepFilters: boolean) => void;
  hasSelectedTrip: boolean;
  isFiltersPanelOpen: boolean;
  isInitialTripSelectionLoading: boolean;
  isSelectTripLoading: boolean;
  isSelfServiceRebooking: boolean;
  onSearchComplete: (data: SearchOnResultPageQuery) => void;
  onShowAncillaryBundleModal: () => void;
  onShowValidWithVoucher: (shouldShowValidWithVoucher: boolean) => void;
  onSort: (sortType?: SortType) => void;
  redirectToDBF?: () => void;
  redirectToTravelerDetails?: () => void;
  removeSavedTrip: (selectionKey: string) => void;
  savedTrips: any[];
  searchData: TransformSearchResultDataReturnType;
  selectedSortType?: string;
  setIsSearchLoading: (searchLoading: boolean) => void;
  setShouldRefetch: (shouldRefetch: boolean) => void;
  setShowTripIsNotAvailableModal: (isShowTripIsNotAvailableModal: boolean) => void;
  shouldRefetch: boolean;
  shouldShowLoadingSpinner?: boolean;
  shouldShowTripIsNotAvailableModal: boolean;
  toggleFiltersPanel: () => void;
  toggleSearchPanel: () => void;
}

type SearchResultsCommonPropsType = SearchResultsGraphqlContainerProps & {
  onCloseTripNotAvailableModal: () => void;
  quickSortPrices: {
    bestValue: number;
    carrierPromo: number;
    cheapTrip: number;
    recommendation: number;
    shortTrip: number;
  } | null;
  search: TransformSearchResultDataReturnType;
  setFetchMoreResults: () => void;
  shouldShowValidWithVoucher: boolean;
};

type SearchResultsV3PropsType = SearchResultsCommonPropsType & {
  isSearchLoading: boolean;
  shouldShowResultsLoader: boolean;
  toggleSearchPanel: () => void;
};

const SearchResultsGraphqlContainer = ({
  addSavedTrip,
  addTrip,
  closeFiltersPanel,
  handleSearch,
  hasSelectedTrip,
  isFiltersPanelOpen,
  isInitialTripSelectionLoading,
  isSelectTripLoading,
  isSelfServiceRebooking,
  onSearchComplete,
  onShowAncillaryBundleModal,
  onShowValidWithVoucher,
  onSort,
  redirectToDBF,
  redirectToTravelerDetails,
  removeSavedTrip,
  savedTrips,
  searchData,
  selectedSortType,
  setIsSearchLoading,
  setShouldRefetch,
  setShowTripIsNotAvailableModal,
  shouldRefetch,
  shouldShowLoadingSpinner,
  shouldShowTripIsNotAvailableModal,
  toggleFiltersPanel,
  toggleSearchPanel,
}: SearchResultsGraphqlContainerProps) => {
  const { p } = useProperty();
  const location = useLocation();
  const { searchVariables } = useSearch();
  const [shouldFetchMoreResults, setShouldFetchMoreResults] = useState(false);
  const isInitialRenderRef = useRef(true);

  const isMetaEntry = Boolean(location.search);
  const isMultiStop = searchData?.type !== ONE_WAY && searchData?.type !== RETURN;
  const isResultPageV3 = String(p('Result.Page.Version')) === RESULT_PAGE_VERSION_3;
  const isSkeletonEnabled = p('Result.Skeleton.Enabled');

  const {
    data,
    loading: searchLoading,
    fetchMore,
    networkStatus,
    refetch,
  } = useQuery(SEARCH_QUERY, {
    variables: searchVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    onError: ({ graphQLErrors, networkError }) => {
      const fallbackUrl = getFallbackUrl();
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      } else {
        if (networkError) {
          throw new Error(`[Network error]: ${networkError}`);
        }
        const queryErrors = graphQLErrors.map((error) => error?.message).join(',');
        throw new Error(
          `Invalid response while fetching search results: ${queryErrors}. More info: ${JSON.stringify(
            userInfo(),
          )}`,
        );
      }
    },
  });

  if (isInitialRenderRef.current) {
    isInitialRenderRef.current = false;
    if (data) {
      refetch();
    }
  }

  useEffect(() => {
    if (data && !searchLoading) {
      onSearchComplete(data);
      setShouldFetchMoreResults(false);
    }
    setIsSearchLoading(searchLoading);
  }, [data, onSearchComplete, searchLoading, setIsSearchLoading]);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [refetch, setShouldRefetch, shouldRefetch]);

  const handleCloseTripNotAvailableModal = () => {
    setShowTripIsNotAvailableModal(false);
    refetch();
  };

  useEffect(() => {
    const getOffset = () => searchData?.flights?.length || 0;
    const handleFetchMoreResults = () => {
      fetchMore({
        query: SEARCH_MORE_QUERY,
        variables: {
          ...searchVariables,
          offset: getOffset(),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            ...prev,
            search: {
              ...prev.search,
              flights: [
                ...(prev?.search?.flights || []),
                ...(fetchMoreResult?.search?.flights || []),
              ],
            },
          } as SearchOnResultPageQuery;
        },
      }).catch(console.error); // eslint-disable-line no-console
    };

    if (shouldFetchMoreResults) {
      handleFetchMoreResults();
      setShouldFetchMoreResults(false);
    }
  }, [fetchMore, shouldFetchMoreResults, searchData?.flights?.length, searchVariables]);

  if (!searchData || isInitialTripSelectionLoading) {
    if (isResultPageV3 && isSkeletonEnabled) {
      return <ResultSkeletonV3 />;
    }
    return <SearchTransition />;
  }

  const isFetchMoreState = (networkStatus === NetworkStatus.fetchMore) as boolean;
  const showSkeletonPage = searchLoading && !isFetchMoreState;
  if (showSkeletonPage && !isResultPageV3) {
    return <SearchResultsSkeleton />;
  }

  const defaultSortType = p('Result.QuickSort.Cheapest.Enabled')
    ? quickSortType.CHEAPEST
    : quickSortType.RECOMMENDED;
  const isQuickBarDropDownSelected = localStorage.getItem('isQuickBarDropDownSelected');
  if (!isQuickBarDropDownSelected) {
    if (
      showSkeletonPage &&
      isResultPageV3 &&
      searchVariables?.filters?.length === 0 &&
      searchVariables.sortTypeCode === defaultSortType
    ) {
      if (isSkeletonEnabled) {
        return <ResultSkeletonV3 />;
      }
      return <SearchTransition />;
    }
  }

  const searchResultsCommonProps = {
    addTrip,
    isSelectTripLoading,
    isSelfServiceRebooking,
    onCloseTripNotAvailableModal: handleCloseTripNotAvailableModal,
    onShowAncillaryBundleModal,
    onShowValidWithVoucher,
    onSort,
    quickSortPrices:
      searchData && searchData.quickSortPrices
        ? transformQuickSortPrices(searchData.quickSortPrices)
        : null,
    redirectToDBF,
    redirectToTravelerDetails,
    search: searchData,
    selectedSortType,
    setFetchMoreResults: setShouldFetchMoreResults,
    shouldShowTripIsNotAvailableModal,
    shouldShowValidWithVoucher: searchData.validWithVoucher,
  } as SearchResultsCommonPropsType;

  const searchResultsV3Props = {
    ...searchResultsCommonProps,
    addSavedTrip,
    closeFiltersPanel,
    handleSearch,
    hasSelectedTrip,
    isFiltersPanelOpen,
    isSearchLoading: searchLoading,
    removeSavedTrip,
    savedTrips,
    shouldShowResultsLoader: showSkeletonPage,
    toggleFiltersPanel,
    toggleSearchPanel,
  } as SearchResultsV3PropsType;

  let searchResultsPage;

  if (isResultPageV3) {
    searchResultsPage =
      showSkeletonPage && !isSkeletonEnabled ? (
        <SearchTransition />
      ) : (
        <SearchResultsV3 {...searchResultsV3Props} />
      );
  } else {
    searchResultsPage = (
      <SearchResults
        {...searchResultsCommonProps}
        flightResults={searchData.flights}
        isMultiStop={isMultiStop}
        shouldShowLoadingSpinner={shouldShowLoadingSpinner}
        tripCampaigns={searchData.tripCampaigns}
      />
    );
  }

  const FallBackComponent = () => {
    return ((isResultPageV3 && isMetaEntry) || isResultPageV3) && isSkeletonEnabled ? (
      <ResultSkeletonV3 />
    ) : (
      <SearchTransition />
    );
  };
  return <Suspense fallback={<FallBackComponent />}>{searchResultsPage}</Suspense>;
};

export default SearchResultsGraphqlContainer;
